import { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import GeneralDialog from '../../../../shared/general-dialog';
import { useState } from 'react';
import { UserFormData } from '../../add-user-dialog/user-form-page';

import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

interface AddressEditDialogProps {
  openAddressDialog: boolean;
  setOpenAddressDialog: (value: boolean) => void;
  clickedIndex: number;
  setValue: UseFormSetValue<UserFormData>;
  disabled: boolean;
  getValues: UseFormGetValues<UserFormData>;
}

export default function AddressEditDialog({
  openAddressDialog,
  setOpenAddressDialog,
  clickedIndex,
  setValue,
  disabled,
  getValues,
}: AddressEditDialogProps) {
  const [poBox, setPoBox] = useState(() =>
    getValues(`addresses.${clickedIndex}.poBox`)
  );
  const [address, setAddress] = useState(() =>
    getValues(`addresses.${clickedIndex}.streetAddress`)
  );
  const [extendedAddress, setExtendedAddress] = useState(() =>
    getValues(`addresses.${clickedIndex}.extendedAddress`)
  );
  const [locality, setLocality] = useState(() =>
    getValues(`addresses.${clickedIndex}.locality`)
  );
  const [region, setRegion] = useState(() =>
    getValues(`addresses.${clickedIndex}.region`)
  );
  const [postalCode, setPostalCode] = useState(() =>
    getValues(`addresses.${clickedIndex}.postalCode`)
  );
  const [country, setCountry] = useState(() =>
    getValues(`addresses.${clickedIndex}.country`)
  );
  const [formatted, setFormatted] = useState(() =>
    getValues(`addresses.${clickedIndex}.formatted`)
  );

  const hasPoBox = poBox !== '';
  const hasStreetAddress = address !== '';
  const hasExtendedAddress = extendedAddress !== '';
  const hasLocality = locality !== '';
  const hasRegion = region !== '';
  const hasPostalCode = postalCode !== '';
  const hasCountry = country !== '';

  const formattedValue = `${hasPoBox ? poBox + '\r\n' : ''}${
    hasStreetAddress ? address + '\r\n' : ''
  }${hasExtendedAddress ? extendedAddress + '\r\n' : ''}${
    hasLocality ? locality + ',' : ''
  }${hasRegion ? ` ${region}` : ''}${hasPostalCode ? ` ${postalCode}` : ''}${
    hasCountry ? ` ${country}` : ''
  }`;

  const formValuesHaveChanged =
    poBox !== getValues(`addresses.${clickedIndex}.poBox`) ||
    address !== getValues(`addresses.${clickedIndex}.streetAddress`) ||
    extendedAddress !==
      getValues(`addresses.${clickedIndex}.extendedAddress`) ||
    locality !== getValues(`addresses.${clickedIndex}.locality`) ||
    region !== getValues(`addresses.${clickedIndex}.region`) ||
    postalCode !== getValues(`addresses.${clickedIndex}.postalCode`) ||
    country !== getValues(`addresses.${clickedIndex}.country`) ||
    formatted !== getValues(`addresses.${clickedIndex}.formatted`);

  return (
    <GeneralDialog
      open={openAddressDialog}
      handleClose={() => {
        setOpenAddressDialog(false);
      }}
      title={'Edit Address'}
      dialogContent={
        <Stack sx={{ px: 1 }} gap={4}>
          <Grid container spacing={4}>
            <Grid size={{ xs: 6 }}>
              <TextField
                value={poBox}
                onChange={(e) => {
                  setPoBox(e.target.value);
                }}
                disabled={disabled}
                autoComplete="off"
                label="PO Box"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                value={address}
                onChange={(e) => {
                  setAddress(e.target.value);
                }}
                label="Street Address 1"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                value={extendedAddress}
                onChange={(e) => {
                  setExtendedAddress(e.target.value);
                }}
                label="Street Address 2"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                value={locality}
                onChange={(e) => {
                  setLocality(e.target.value);
                }}
                label="City"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                value={region}
                onChange={(e) => {
                  setRegion(e.target.value);
                }}
                label="State"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                value={postalCode}
                onChange={(e) => {
                  setPostalCode(e.target.value);
                }}
                label="ZIP Code"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                value={country}
                onChange={(e) => {
                  setCountry(e.target.value);
                }}
                label="Country"
                fullWidth
              />
            </Grid>
          </Grid>
          <Stack gap={3}>
            <Tooltip title="Use the fields above to generate formatted address">
              <Button
                onClick={() => {
                  setFormatted(formattedValue);
                }}
                variant="contained"
                sx={{ width: 'fit-content' }}
              >
                Format
              </Button>
            </Tooltip>
            <TextField
              value={formatted}
              onChange={(e) => setFormatted(e.target.value)}
              label="Formatted Address"
              fullWidth
              multiline
              rows={4}
            />
          </Stack>
        </Stack>
      }
      primaryButton={{
        loading: false,
        action: () => {
          if (formValuesHaveChanged) {
            setValue(`addresses.${clickedIndex}.poBox`, poBox, {
              shouldDirty: true,
            });
            setValue(`addresses.${clickedIndex}.streetAddress`, address, {
              shouldDirty: true,
            });
            setValue(
              `addresses.${clickedIndex}.extendedAddress`,
              extendedAddress,
              { shouldDirty: true }
            );
            setValue(`addresses.${clickedIndex}.locality`, locality, {
              shouldDirty: true,
            });
            setValue(`addresses.${clickedIndex}.region`, region, {
              shouldDirty: true,
            });
            setValue(`addresses.${clickedIndex}.postalCode`, postalCode, {
              shouldDirty: true,
            });
            setValue(`addresses.${clickedIndex}.country`, country, {
              shouldDirty: true,
            });
            setValue(`addresses.${clickedIndex}.formatted`, formatted, {
              shouldDirty: true,
            });
          }

          setOpenAddressDialog(false);
        },
        disabled: !formValuesHaveChanged,
        title: 'Update',
      }}
    />
  );
}
